/**
 * Upsert chat conversation — what the client posts to `POST /api/chat/upsert`.
 *
 * The route is a thin wrapper around two RPCs:
 *   `restore_conversation_if_deleted(p_room_name, p_user_id)`
 *   `upsert_conversation_and_participants(p_room_name, p_participant_ids)`
 *
 * Body shape is two fields — `roomName` (colon-joined participant ids; the
 * route does NOT canonicalise / sort, callers do so themselves before posting)
 * and `participantIds` (the same ids, parsed back to an array). At least one
 * participant is required; the empty-array case the previous ad-hoc validator
 * rejected as `participantIds are required` becomes a Zod `min(1)` issue.
 *
 * Mobile-consumed (api-messaging.ts upsertChatConversation). The web producers
 * pass `participantIds` derived from `roomName.split(':')` — sometimes the raw
 * order, sometimes pre-sorted — the schema is intentionally wire-truthful and
 * does NOT enforce ordering. The route doesn't either; canonicalisation is a
 * delivery-layer concern that pre-dates this schema.
 *
 * @see app/api/chat/upsert/route.ts — handler
 * @see apps/mobile/lib/api-messaging.ts upsertChatConversation — mobile caller
 * @see lib/chat/chat-upsert.ts — web canonical builder
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-E B1
 * @see packages/contract/src/chat-send.ts — sibling endpoint (also chat surface)
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface ChatUpsertRequest {
  /**
   * Colon-joined participant ids. The route does not normalise; producers
   * either canonicalise (sort) before posting or pass the raw form — both are
   * wire-truthful and downstream RPCs key on this value as-is.
   */
  roomName: string;
  /**
   * The same participants as `roomName.split(':')`. At least one id is
   * required (the legacy validator rejected `!participantIds?.length`).
   */
  participantIds: string[];
}

export const ChatUpsertRequestSchema = pinned<ChatUpsertRequest>()(
  z.object({
    roomName: z.string().min(1),
    participantIds: z.array(z.string().min(1)).min(1),
  }),
);
